/**
 * Embed-host bridge.
 *
 * When Studio boots inside an iframe (dev harness or Angular host in
 * production), the parent hands over the runtime configuration over a
 * `postMessage` handshake instead of pre-populating `window.LOOKER_CONFIG`
 * synchronously.
 *
 * Flow:
 *   1. Studio calls `waitForEmbedConfig()` on boot.
 *   2. This module posts `studio:ready` to the parent (origin pinned via
 *      the `#parentOrigin=` hash the parent sets when creating the iframe).
 *   3. The parent replies with `studio:init` carrying a `LookerWindowConfig`
 *      payload. We write it to `window.LOOKER_CONFIG` — the internal seed
 *      contract used by every hook / provider — and resolve.
 *   4. Any subsequent `studio:update` messages patch `window.LOOKER_CONFIG`
 *      in place (token refresh, deeplink change, etc.).
 *
 * All outbound `postMessage` calls target the pinned parent origin —
 * never `'*'`. Inbound messages are filtered against a strict allowlist.
 */

import type { LookerWindowConfig } from '../types/windowConfig';
import { getRuntimeAppConfig } from './runtimeConfig';

const EMBED_PROTOCOL_VERSION = 1;
const STUDIO_VERSION = process.env.STUDIO_VERSION ?? 'dev';

// Debug logging. Enabled by default in dev; toggleable at runtime with
//   localStorage.setItem('studio.embed.debug', '1')  // force on
//   localStorage.setItem('studio.embed.debug', '0')  // force off
const isDebugEnabled = (): boolean => {
  try {
    const override = window.localStorage.getItem('studio.embed.debug');
    if (override === '1') return true;
    if (override === '0') return false;
  } catch {
    /* localStorage unavailable */
  }
  return process.env.NODE_ENV !== 'production';
};

const debugLog = (direction: '→ parent' | '← parent', payload: unknown): void => {
  if (!isDebugEnabled()) return;
  console.debug(`[embed] studio ${direction}`, payload);
};

// Origins the parent is allowed to send messages from.
//
// Baseline is same-origin only. Everything else comes from
// `window.APP_CONFIG.parentOrigins` (semicolon-separated, populated by
// Cloud Run env → nginx envsubst → /config.js at runtime; see
// runtimeConfig.ts). Each env decides its own set without a rebuild —
// e.g. dev's PARENT_ORIGINS can include `https://localhost:8080` for the
// remote harness, while prod's cannot.
const buildAllowedParentOrigins = (): ReadonlySet<string> => {
  const runtime = getRuntimeAppConfig();
  const fromRuntime = runtime?.parentOrigins ? runtime.parentOrigins.split(';').map((s) => s.trim()) : [];
  return new Set([typeof window !== 'undefined' ? window.location.origin : '', ...fromRuntime].filter(Boolean));
};

const ALLOWED_PARENT_ORIGINS: ReadonlySet<string> = buildAllowedParentOrigins();

interface StudioInitMessage {
  type: 'studio:init';
  payload: LookerWindowConfig;
}
interface StudioUpdateMessage {
  type: 'studio:update';
  patch: Partial<LookerWindowConfig>;
}
interface StudioNavigateMessage {
  type: 'studio:navigate';
  path: string;
  search?: string;
  state?: unknown;
}
type StudioInboundMessage = StudioInitMessage | StudioUpdateMessage | StudioNavigateMessage;

// Quick pre-filter to skip noise from React DevTools, MSW, browser
// extensions, etc. — they blast messages at every window and pollute the
// debug log if we treat them as protocol violations.
const looksLikeStudioMessage = (candidate: unknown): candidate is { type: string } =>
  !!candidate &&
  typeof candidate === 'object' &&
  typeof (candidate as { type?: unknown }).type === 'string' &&
  (candidate as { type: string }).type.startsWith('studio:');

const isInboundMessage = (candidate: unknown): candidate is StudioInboundMessage =>
  looksLikeStudioMessage(candidate) &&
  (candidate.type === 'studio:init' || candidate.type === 'studio:update' || candidate.type === 'studio:navigate');

/**
 * Consumers subscribe to this DOM event to receive `studio:navigate`
 * requests from the parent (browser back/forward → parent → Studio).
 * The detail carries the target path + search string.
 */
export const STUDIO_NAVIGATE_EVENT = 'studio:embed-navigate';
export interface StudioNavigateEventDetail {
  path: string;
  search: string;
  state?: unknown;
}

export const isEmbedded = (): boolean => {
  try {
    return typeof window !== 'undefined' && window.parent !== window;
  } catch {
    return true;
  }
};

// The parent's origin isn't known cross-origin without help. Two sources:
//  1. `#parentOrigin=…` hash fragment set by the parent when it created the
//     iframe (used for the initial `studio:ready` before we've seen any
//     inbound message).
//  2. The origin of the first inbound message we accept (authoritative from
//     that point on).
const readParentOriginFromHash = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const advertised = params.get('parentOrigin');
    return advertised && ALLOWED_PARENT_ORIGINS.has(advertised) ? advertised : null;
  } catch {
    return null;
  }
};

let parentOrigin: string | null = readParentOriginFromHash();

export const emitToParent = (msg: Record<string, unknown>): void => {
  if (!isEmbedded() || !parentOrigin) return;
  debugLog('→ parent', { origin: parentOrigin, msg });
  try {
    window.parent.postMessage(msg, parentOrigin);
  } catch (err) {
    debugLog('→ parent', { error: 'postMessage failed', err });
  }
};

/**
 * Notify the parent that Studio's internal route changed so it can mirror
 * the URL (e.g. update the browser address bar). No-op when not embedded.
 */
export const notifyNavigation = (path: string, search = '', state?: unknown): void => {
  emitToParent({ type: 'studio:navigation', path, search, state });
};

const applyUpdate = (patch: Partial<LookerWindowConfig>): void => {
  if (!window.LOOKER_CONFIG) return;
  window.LOOKER_CONFIG = { ...window.LOOKER_CONFIG, ...patch };
};

/**
 * Post `studio:ready`, wait for `studio:init` from the parent, populate
 * `window.LOOKER_CONFIG`, and resolve. The same listener stays attached
 * for the lifetime of the tab so `studio:update` patches keep working
 * after the initial boot.
 */
export const waitForEmbedConfig = (): Promise<void> =>
  new Promise((resolve) => {
    const handler = (evt: MessageEvent): void => {
      // Silently drop noise that isn't remotely our protocol.
      if (!looksLikeStudioMessage(evt.data)) return;

      if (!ALLOWED_PARENT_ORIGINS.has(evt.origin)) {
        debugLog('← parent', { dropped: 'origin not allowed', origin: evt.origin, data: evt.data });
        return;
      }
      if (!isInboundMessage(evt.data)) {
        debugLog('← parent', { dropped: 'type not recognized', origin: evt.origin, data: evt.data });
        return;
      }

      debugLog('← parent', { origin: evt.origin, data: evt.data });

      // Pin future outbound sends to the origin we're actually talking to.
      parentOrigin = evt.origin;

      if (evt.data.type === 'studio:init') {
        window.LOOKER_CONFIG = evt.data.payload;
        resolve();
        return;
      }
      if (evt.data.type === 'studio:update') {
        applyUpdate(evt.data.patch);
        return;
      }
      if (evt.data.type === 'studio:navigate') {
        const detail: StudioNavigateEventDetail = {
          path: evt.data.path,
          search: evt.data.search ?? '',
          state: evt.data.state,
        };
        window.dispatchEvent(new CustomEvent<StudioNavigateEventDetail>(STUDIO_NAVIGATE_EVENT, { detail }));
      }
    };
    window.addEventListener('message', handler);
    emitToParent({
      type: 'studio:ready',
      version: STUDIO_VERSION,
      protocolVersion: EMBED_PROTOCOL_VERSION,
    });
  });
